import { useEffect, useCallback } from 'react';

/**
 * Custom hook for keyboard navigation in SOC Dashboard
 * Handles arrow keys for navigation, space for pause/resume, and F11 for fullscreen
 * 
 * @param {Object} options - Configuration options
 * @param {Function} options.onNext - Callback for next action (right arrow)
 * @param {Function} options.onPrevious - Callback for previous action (left arrow)
 * @param {Function} options.onTogglePause - Callback for pause/resume (spacebar)
 * @param {Function} options.onToggleFullscreen - Callback for fullscreen toggle (F11)
 * @param {boolean} options.enabled - Whether keyboard navigation is enabled
 */
const useKeyboardNav = ({
  onNext,
  onPrevious,
  onTogglePause,
  onToggleFullscreen,
  enabled = true
}) => {
  /**
   * Toggles fullscreen mode for the document
   */
  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      // Enter fullscreen
      if (document.documentElement.requestFullscreen) {
        document.documentElement.requestFullscreen();
      } else if (document.documentElement.webkitRequestFullscreen) {
        // Safari
        document.documentElement.webkitRequestFullscreen();
      } else if (document.documentElement.msRequestFullscreen) {
        // IE11
        document.documentElement.msRequestFullscreen();
      }
    } else {
      // Exit fullscreen
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        // Safari
        document.webkitExitFullscreen();
      } else if (document.msExitFullscreen) {
        // IE11
        document.msExitFullscreen();
      }
    }
  }, []);

  /**
   * Handles keyboard events and dispatches appropriate callbacks
   */
  const handleKeyDown = useCallback((event) => {
    if (!enabled) return;

    // Prevent default behavior for handled keys
    switch (event.key) {
      case 'ArrowRight':
      case 'Right':
        event.preventDefault();
        if (onNext) onNext();
        break;

      case 'ArrowLeft':
      case 'Left':
        event.preventDefault();
        if (onPrevious) onPrevious();
        break;

      case ' ':
        event.preventDefault();
        if (onTogglePause) onTogglePause();
        break;

      case 'F11':
        event.preventDefault();
        if (onToggleFullscreen) {
          onToggleFullscreen();
        } else {
          // Default F11 behavior - toggle fullscreen
          toggleFullscreen();
        }
        break;

      default:
        // Do nothing for other keys
        break;
    }
  }, [enabled, onNext, onPrevious, onTogglePause, onToggleFullscreen, toggleFullscreen]);

  /**
   * Set up keyboard event listeners on mount and clean up on unmount
   */
  useEffect(() => {
    if (!enabled) return;

    window.addEventListener('keydown', handleKeyDown);

    // Cleanup function to remove event listener
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [enabled, handleKeyDown]);

  return {
    toggleFullscreen
  };
};

export default useKeyboardNav;
